/**
 * Saved scenarios: cut one, and see the ones already cut.
 *
 * WHY THIS EXISTS. The snapshots API has been complete since the persistence
 * layer landed — list, fetch, and a POST that verifies the caller computed
 * against the book that exists now — and nothing has ever called it. A scenario
 * lived in memory and died on reload, so "model where a new person helps most"
 * meant holding two answers in your head, one of which you could not see any
 * more.
 *
 * WHAT A SNAPSHOT IS FOR, and why it is worth the ceremony: it is the module's
 * audit trail. Config version, pins, slots, the subset of the CRM it covered and
 * a digest of the book are all stored beside the result, so anyone holding the
 * engine can replay the run and compare. That is the whole reason the save can
 * fail — see the 409 below.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@andavo/web-ui';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { fetchSnapshot, fetchSnapshots, type SnapshotSummaryDto } from '../api';
import { saveSnapshot, StaffingWriteError } from '../mutations';
import { snapshotBlocker, toSnapshotRequest } from '../lib/snapshot';
import { describeUnresolved, restoreScenario } from '../lib/restore';
import { useStaffingData } from '../StaffingDataProvider';
import type { StaffingModel } from '../lib/model';
import type { StaffingReadSet } from '../api';

export function SnapshotPanel({
  read,
  model,
  includeInactive,
  scenarioChanges,
}: {
  read: StaffingReadSet | null;
  model: StaffingModel | null;
  includeInactive: boolean;
  scenarioChanges: number;
}) {
  const { can } = useMe();
  const { setScenario } = useStaffingData();
  const [saved, setSaved] = useState<SnapshotSummaryDto[] | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [restored, setRestored] = useState<{
    label: string;
    notes: string[];
  } | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<StaffingWriteError | null>(null);
  const [justSaved, setJustSaved] = useState<SnapshotSummaryDto | null>(null);

  // Reading the list needs only view:staffing, which everyone who can open this
  // module holds. Cutting one needs both authorities cm-api demands, and asking
  // for them separately would offer a button that always 403s.
  const canSave = can('manage:staffing') && can('view:staffing_financials');

  const loadList = useCallback(async () => {
    try {
      setSaved(await fetchSnapshots());
      setListError(null);
    } catch {
      setListError('Could not load saved scenarios.');
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const blocker = snapshotBlocker(read, model);

  const save = async () => {
    if (!read || !model) return;
    setBusy(true);
    setError(null);
    try {
      const created = await saveSnapshot(
        toSnapshotRequest({
          read,
          model,
          includeInactive,
          label: label || null,
        }),
      );
      setJustSaved(created);
      setLabel('');
      await loadList();
    } catch (err) {
      setError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not save this scenario.', 0),
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Load a saved scenario as a what-if over TODAY's book.
   *
   * Not "go back to" — the book has moved on, and seeing how much is usually the
   * reason for doing this at all. So it lands as a scenario like any other:
   * visible in the what-if banner, discardable, and writing nothing.
   */
  const restore = async (summary: SnapshotSummaryDto) => {
    if (!read) return;
    setRestoring(summary.id);
    setRestored(null);
    setError(null);
    try {
      const full = await fetchSnapshot(summary.id);
      const { scenario, unresolved } = restoreScenario(full, {
        roster: read.roster.roster,
        customerIds: new Set(read.accounts.accounts.map((a) => a.id)),
      });

      const departed = new Map(
        read.roster.roster.map((m) => [m.id, m.displayName]),
      );
      const notes = describeUnresolved(
        unresolved,
        // A departed CSM is BY DEFINITION absent from the roster we hold, so
        // there is no name to look up. The id is what we have, and printing it
        // beats printing "undefined".
        (csmId) => departed.get(csmId) ?? `CSM ${csmId}`,
      );

      // The configuration is NOT restored — only the seats and pins are. Tier
      // bands live on a config version, and silently reshaping them would
      // re-tier the whole book without anyone asking. If they have changed since,
      // that changes what this scenario means, so it is said rather than hidden.
      const current = read.config.config?.version;
      if (current !== undefined && current !== full.configVersion) {
        notes.push(
          `Saved against configuration v${full.configVersion}; the tier bands in force now are v${current}. The seats and pins were restored, the bands were not — so this scenario is that team over today's tiers.`,
        );
      }

      setScenario(scenario);
      setRestored({ label: summary.label ?? 'Unnamed', notes });
    } catch (err) {
      setError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not restore that scenario.', 0),
      );
    } finally {
      setRestoring(null);
    }
  };

  return (
    <section className="flex flex-col gap-4" data-testid="staffing-snapshots">
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-base text-content-primary">
          Saved scenarios
        </h2>
        <p className="text-content-secondary text-sm">
          A snapshot records what the model said and the inputs it said it from
          — the configuration version, the pins and seats in force, which
          customers the book covered, and a digest of the book itself. It
          changes nothing.
        </p>
      </div>

      {canSave ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="font-medium text-content-secondary text-xs">
                Name this scenario
              </span>
              <input
                className="w-72 rounded-md border border-border-medium bg-surface-background px-2 py-1 text-sm"
                disabled={busy}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Third Small CSM, Q1"
                value={label}
              />
            </label>
            <Button
              disabled={busy || blocker !== null}
              onClick={save}
              size="sm"
            >
              {busy ? 'Saving…' : 'Save this scenario'}
            </Button>
          </div>

          {blocker ? (
            <p className="text-content-secondary text-xs">{blocker}</p>
          ) : (
            <p className="text-content-secondary text-xs">
              {scenarioChanges > 0
                ? `Saves the ${scenarioChanges} what-if ${scenarioChanges === 1 ? 'change' : 'changes'} currently applied, as computed.`
                : 'Saves the model as currently stored — no what-if changes are applied.'}
            </p>
          )}
        </div>
      ) : (
        <p className="text-content-secondary text-sm">
          Saving a scenario needs <code>manage:staffing</code> and{' '}
          <code>view:staffing_financials</code> — a snapshot carries per-CSM
          load, which the cost model prices.
        </p>
      )}

      {error ? (
        <div className="flex flex-col gap-1" role="alert">
          <p className="text-content-danger text-sm">{error.message}</p>
          {error.remedy ? (
            <p className="text-content-danger text-xs">{error.remedy}</p>
          ) : null}
          {error.problems.map((p) => (
            <p className="text-content-danger text-xs" key={p}>
              {p}
            </p>
          ))}
        </div>
      ) : null}

      {justSaved ? (
        <p className="text-content-success text-sm" role="status">
          Saved “{justSaved.label ?? 'Unnamed'}” over{' '}
          {justSaved.bookAccountCount} customers.
        </p>
      ) : null}

      {restored ? (
        <div
          className="flex flex-col gap-1 rounded-md border border-border-medium bg-surface-background px-3 py-2"
          data-testid="staffing-snapshot-restored"
          role="status"
        >
          {/* BRANCHED, because the no-difference case is the interesting one and
              the other wording is simply wrong there. Restoring a scenario cut
              from the state still in force produces an overlay identical to it,
              so `scenarioChanges` is 0 and the what-if banner — which this used
              to tell the reader to go and use — never appears. Found by
              clicking it. "Nothing differs" is also a real answer: it says the
              team and the pins have not moved since that scenario was cut.

              The control named is Overview's own "Reset to saved model", NOT
              the what-if banner: ScenarioBar mounts on Portfolios only, so the
              first wording sent the reader to something not on this page. Also
              found by clicking, immediately after the first fix. */}
          <p className="text-content-primary text-sm">
            {scenarioChanges > 0 ? (
              <>
                Loaded “{restored.label}” as a what-if over today’s book —{' '}
                {scenarioChanges} {scenarioChanges === 1 ? 'change' : 'changes'}{' '}
                from what is stored. Nothing has been saved — “Reset to saved
                model”, above, puts it back.
              </>
            ) : (
              <>
                Loaded “{restored.label}”. Nothing in it differs from the model
                as stored — the seats and pins it recorded are still the ones in
                force.
              </>
            )}
          </p>
          {/* Every line here is something the restore could NOT reproduce.
              Rendered next to the success message rather than as an error,
              because the scenario did load; it just does not mean quite what it
              meant when it was cut. */}
          {restored.notes.map((n) => (
            <p className="text-content-warning text-xs" key={n}>
              {n}
            </p>
          ))}
        </div>
      ) : null}

      {listError ? (
        <p className="text-content-danger text-sm">{listError}</p>
      ) : null}

      <SnapshotList
        onRestore={restore}
        restoring={restoring}
        snapshots={saved}
      />
    </section>
  );
}

/**
 * The list.
 *
 * SUBSET IS SHOWN ON EVERY ROW, not tucked into a detail view. Two snapshots
 * that differ only by which customers the book covered are records about
 * different books, and they are otherwise indistinguishable — same date, same
 * config version, same author. Leaving it out would invite exactly the
 * comparison that cannot be made.
 */
function SnapshotList({
  snapshots,
  onRestore,
  restoring,
}: {
  snapshots: SnapshotSummaryDto[] | null;
  onRestore: (s: SnapshotSummaryDto) => void;
  restoring: string | null;
}) {
  if (snapshots === null) {
    return <p className="text-content-secondary text-sm">Loading…</p>;
  }
  if (snapshots.length === 0) {
    return (
      <p
        className="text-content-secondary text-sm"
        data-testid="staffing-snapshots-empty"
      >
        No scenarios saved yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border-medium border-b text-content-secondary text-xs">
            <th className="py-1 pr-3 text-left font-medium">Scenario</th>
            <th className="py-1 pr-3 text-left font-medium">Saved</th>
            <th className="py-1 pr-3 text-left font-medium">By</th>
            <th className="py-1 pr-3 text-right font-medium">Config</th>
            <th className="py-1 pr-3 text-right font-medium">Customers</th>
            <th className="py-1 pr-3 text-left font-medium">Book</th>
            <th className="py-1 pr-3 text-left font-medium">Engine</th>
            <th className="py-1 text-right font-medium">
              <span className="sr-only">Restore</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => (
            <tr className="border-border-subtle border-b" key={s.id}>
              <td className="py-1.5 pr-3">
                {s.label ?? (
                  <span className="text-content-secondary">Unnamed</span>
                )}
              </td>
              <td className="py-1.5 pr-3 text-content-secondary">
                {new Date(s.createdAt).toLocaleString()}
              </td>
              <td className="py-1.5 pr-3 text-content-secondary">
                {s.createdBy}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                v{s.configVersion}
              </td>
              <td className="py-1.5 pr-3 text-right tabular-nums">
                {s.bookAccountCount}
              </td>
              <td className="py-1.5 pr-3 text-content-secondary">
                {s.includeInactive ? 'incl. inactive' : 'active only'}
              </td>
              <td className="py-1.5 pr-3 text-content-secondary tabular-nums">
                {s.engineVersion}
              </td>
              <td className="py-1.5 text-right">
                <Button
                  disabled={restoring !== null}
                  onClick={() => onRestore(s)}
                  size="sm"
                  variant="secondary"
                >
                  {restoring === s.id ? 'Loading…' : 'Load as what-if'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
